"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TransferRestrictionBase = void 0;
const internal_1 = require("../../../../../internal");
const types_1 = require("../../../../../types");
const conversion_1 = require("../../../../../utils/conversion");
const internal_2 = require("../../../../../utils/internal");
const restrictionTypeToStatType = {
    [types_1.TransferRestrictionType.Count]: types_1.StatType.Count,
    [types_1.TransferRestrictionType.Percentage]: types_1.StatType.Balance,
    [types_1.TransferRestrictionType.ClaimCount]: types_1.StatType.ScopedCount,
    [types_1.TransferRestrictionType.ClaimPercentage]: types_1.StatType.ScopedBalance,
};
/**
 * Base class for managing Transfer Restrictions
 */
class TransferRestrictionBase extends internal_1.Namespace {
    /**
     * @hidden
     */
    constructor(parent, context) {
        super(parent, context);
        this.addRestriction = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [
                internal_1.addTransferRestriction,
                Object.assign(Object.assign({}, args), { type: this.type, asset: parent }),
            ],
        }, context);
        this.setRestrictions = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [
                internal_1.setTransferRestrictions,
                Object.assign(Object.assign({}, args), { type: this.type, asset: parent }),
            ],
        }, context);
        this.removeRestrictions = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: () => [
                internal_1.setTransferRestrictions,
                {
                    restrictions: [],
                    type: this.type,
                    asset: parent,
                },
            ],
            voidArgs: true,
        }, context);
        this.enableStat = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [
                internal_1.addAssetStat,
                Object.assign(Object.assign({}, args), { type: restrictionTypeToStatType[this.type], asset: parent }),
            ],
        }, context);
        this.disableStat = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [
                internal_1.removeAssetStat,
                Object.assign(Object.assign({}, args), { type: restrictionTypeToStatType[this.type], asset: parent }),
            ],
        }, context);
    }
    /**
     * Retrieve all active Transfer Restrictions of the corresponding type
     *
     * @note there is a maximum number of restrictions allowed across all types.
     *   The `availableSlots` property of the result represents how many more restrictions can be added
     *   before reaching that limit
     */
    get() {
        return __awaiter(this, void 0, void 0, function* () {
            const { parent, context: { polymeshApi: { query: { statistics }, consts, }, }, context, type, } = this;
            const rawAssetId = (0, conversion_1.stringToAssetId)(parent.id, context);
            const { requirements } = yield statistics.assetTransferCompliances(rawAssetId);
            const existingRequirementCount = [...requirements].length;
            const filteredRequirements = [...requirements].filter(requirement => {
                if (type === types_1.TransferRestrictionType.Count) {
                    return requirement.isMaxInvestorCount;
                }
                else if (type === types_1.TransferRestrictionType.Percentage) {
                    return requirement.isMaxInvestorOwnership;
                }
                else if (type === types_1.TransferRestrictionType.ClaimCount) {
                    return requirement.isClaimCount;
                }
                else {
                    return requirement.isClaimOwnership;
                }
            });
            const rawAssetKey = { assetId: rawAssetId };
            const rawExemptedLists = yield Promise.all(filteredRequirements.map(req => {
                const { value } = (0, conversion_1.transferConditionToTransferRestriction)(req, context);
                if (req.isClaimCount) {
                    const { claim } = value;
                    return statistics.transferConditionExemptEntities.entries(Object.assign(Object.assign({}, rawAssetKey), { op: (0, conversion_1.transferRestrictionTypeToStatOpType)(types_1.TransferRestrictionType.ClaimCount, context), claimType: claim.type }));
                }
                else if (req.isClaimOwnership) {
                    const { claim } = value;
                    return statistics.transferConditionExemptEntities.entries(Object.assign(Object.assign({}, rawAssetKey), { op: (0, conversion_1.transferRestrictionTypeToStatOpType)(types_1.TransferRestrictionType.ClaimPercentage, context), claimType: claim.type }));
                }
                else {
                    return statistics.transferConditionExemptEntities.entries(Object.assign(Object.assign({}, rawAssetKey), { op: (0, conversion_1.transferRestrictionTypeToStatOpType)(type, context) }));
                }
            }));
            const restrictions = rawExemptedLists.map((list, index) => {
                const exemptedIds = list.map(([{ args: [, scopeId], },]) => (0, conversion_1.identityIdToString)(scopeId) // `ScopeId` and `PolymeshPrimitivesIdentityId` are the same type, so this is fine
                );
                const { value } = (0, conversion_1.transferConditionToTransferRestriction)(filteredRequirements[index], context);
                let restriction;
                if (type === types_1.TransferRestrictionType.Count) {
                    restriction = {
                        count: value,
                    };
                }
                else if (type === types_1.TransferRestrictionType.Percentage) {
                    restriction = {
                        percentage: value,
                    };
                }
                else {
                    const { min, max, claim, issuer } = value;
                    restriction = {
                        min,
                        max,
                        claim,
                        issuer,
                    };
                }
                if (exemptedIds.length) {
                    return Object.assign(Object.assign({}, restriction), { exemptedIds });
                }
                return restriction;
            });
            const maxTransferConditions = (0, conversion_1.u32ToBigNumber)(consts.statistics.maxTransferConditionsPerAsset);
            return {
                restrictions,
                availableSlots: maxTransferConditions.minus(existingRequirementCount),
            };
        });
    }
    /**
     * Retrieve all active Transfer Restrictions of the corresponding type
     *
     * @note there is a maximum number of restrictions allowed across all types.
     *   The `availableSlots` property of the result represents how many more restrictions can be added
     *   before reaching that limit
     */
    getStat() {
        return __awaiter(this, void 0, void 0, function* () {
            const { parent, context, context: { polymeshApi: { query: { statistics }, }, }, type, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(parent, context);
            const currentStats = yield statistics.activeAssetStats(rawAssetId);
            let isSet = false;
            const claims = [];
            const pushClaims = (stat) => {
                const [rawClaimType, rawIssuer] = stat.claimIssuer.unwrap();
                const claimType = (0, conversion_1.meshClaimTypeToClaimType)(rawClaimType);
                const issuer = new internal_1.Identity({ did: (0, conversion_1.identityIdToString)(rawIssuer) }, context);
                claims.push({ claimType, issuer });
            };
            [...currentStats].forEach(stat => {
                const statType = (0, conversion_1.meshStatToStatType)(stat);
                if (type === types_1.TransferRestrictionType.ClaimCount && statType === types_1.StatType.ScopedCount) {
                    isSet = true;
                    pushClaims(stat);
                }
                else if (type === types_1.TransferRestrictionType.ClaimPercentage &&
                    statType === types_1.StatType.ScopedBalance) {
                    isSet = true;
                    pushClaims(stat);
                }
                else if (type === types_1.TransferRestrictionType.Percentage && statType === types_1.StatType.Balance) {
                    isSet = true;
                }
                else if (type === types_1.TransferRestrictionType.Count && statType === types_1.StatType.Count) {
                    isSet = true;
                }
            });
            return Object.assign({ isSet }, (claims.length ? { claims } : {}));
        });
    }
}
exports.TransferRestrictionBase = TransferRestrictionBase;
//# sourceMappingURL=TransferRestrictionBase.js.map